/**
 * A reader for glTF 2.0's binary form (M87), as much of it as the reef's model
 * uses (`scripts/reefModel.ts`, the user's `art/reef/reef.glb`, then their Hawaiian reef kit's models,
 * `art/hawaii-reef/`, M110): its JSON and its binary chunk, float attributes, indices, nodes by
 * translation, rotation and scale, and images by buffer view. Plain arithmetic, so the game reads its model the same in node's
 * tests as in a browser (https://registry.khronos.org/glTF/specs/2.0/glTF-2.0.html,
 * sections 4.4 and 5); anything else the model might hold is refused.
 */

interface Accessor { bufferView: number; byteOffset?: number; componentType: number; count: number; type: string; min?: number[]; max?: number[] }
interface BufferView { byteOffset?: number; byteLength: number; byteStride?: number }
interface Node { name?: string; mesh?: number; children?: number[]; translation?: number[]; rotation?: number[]; scale?: number[]; matrix?: number[]; extras?: Record<string, unknown> }
interface Primitive { attributes: Record<string, number>; indices?: number; material?: number; mode?: number }

export interface GltfJson {
  scene?: number;
  scenes: { nodes: number[] }[];
  nodes: Node[];
  meshes: { name?: string; primitives: Primitive[] }[];
  materials?: { name?: string; doubleSided?: boolean; pbrMetallicRoughness?: { baseColorTexture?: { index: number } }; extras?: Record<string, unknown> }[];
  samplers?: { magFilter?: number; minFilter?: number; wrapS?: number; wrapT?: number }[];
  textures?: { source?: number; sampler?: number }[];
  images?: { name?: string; mimeType: string; bufferView: number }[];
  accessors: Accessor[];
  bufferViews: BufferView[];
}

export interface Glb {
  json: GltfJson;
  bin: Uint8Array;
}

const MAGIC = 0x46546c67;
const JSON_CHUNK = 0x4e4f534a;
const BIN_CHUNK = 0x004e4942;

export function readGlb(bytes: Uint8Array): Glb {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (view.getUint32(0, true) !== MAGIC || view.getUint32(4, true) !== 2) throw new Error('not a glTF 2.0 binary');
  let json: GltfJson | null = null;
  let bin: Uint8Array = new Uint8Array(0);
  for (let at = 12; at < Math.min(bytes.byteLength, view.getUint32(8, true));) {
    const length = view.getUint32(at, true);
    const type = view.getUint32(at + 4, true);
    const body = bytes.subarray(at + 8, at + 8 + length);
    if (type === JSON_CHUNK) json = JSON.parse(new TextDecoder().decode(body)) as GltfJson;
    else if (type === BIN_CHUNK) bin = body;
    at += 8 + length;
  }
  if (!json) throw new Error('a glTF binary with no JSON');
  return { json, bin };
}

const WIDTHS: Record<string, number> = { SCALAR: 1, VEC2: 2, VEC3: 3, VEC4: 4 };

/** An accessor's floats, copied out of the binary; only tightly packed 32-bit floats are read. */
export function floatsOf(glb: Glb, index: number): Float32Array {
  const accessor = glb.json.accessors[index];
  if (!accessor || accessor.componentType !== 5126) throw new Error(`accessor ${index} is not 32-bit floats`);
  const view = glb.json.bufferViews[accessor.bufferView] as BufferView;
  const width = WIDTHS[accessor.type] ?? 0;
  if (view.byteStride && view.byteStride !== width * 4) throw new Error(`accessor ${index} is interleaved`);
  const from = (view.byteOffset ?? 0) + (accessor.byteOffset ?? 0);
  const out = new Float32Array(accessor.count * width);
  new Uint8Array(out.buffer).set(glb.bin.subarray(from, from + out.byteLength));
  return out;
}

/** An accessor's indices, unsigned 8-, 16- or 32-bit integers, copied out (M110: the Hawaiian reef kit's models are indexed). */
export function indicesOf(glb: Glb, index: number): Uint32Array {
  const accessor = glb.json.accessors[index];
  const size = accessor?.type === 'SCALAR' ? ({ 5121: 1, 5123: 2, 5125: 4 } as Record<number, number>)[accessor.componentType] : undefined;
  if (!accessor || !size) throw new Error(`accessor ${index} is not unsigned integer indices`);
  const view = glb.json.bufferViews[accessor.bufferView] as BufferView;
  if (view.byteStride && view.byteStride !== size) throw new Error(`accessor ${index} is interleaved`);
  const data = new DataView(glb.bin.buffer, glb.bin.byteOffset + (view.byteOffset ?? 0) + (accessor.byteOffset ?? 0), accessor.count * size);
  return Uint32Array.from({ length: accessor.count }, (_, i) => (size === 1 ? data.getUint8(i) : size === 2 ? data.getUint16(i * 2, true) : data.getUint32(i * 4, true)));
}

/** A buffer view's bytes, an image's. */
export function bytesOf(glb: Glb, bufferView: number): Uint8Array {
  const view = glb.json.bufferViews[bufferView] as BufferView;
  return glb.bin.subarray(view.byteOffset ?? 0, (view.byteOffset ?? 0) + view.byteLength);
}

/** A node's matrix from its translation, rotation (a unit quaternion) and scale, column by column as glTF and three.js keep it. */
export function nodeMatrix(node: Node): number[] {
  if (node.matrix) return node.matrix.slice();
  const [tx, ty, tz] = (node.translation ?? [0, 0, 0]) as [number, number, number];
  const [x, y, z, w] = (node.rotation ?? [0, 0, 0, 1]) as [number, number, number, number];
  const [sx, sy, sz] = (node.scale ?? [1, 1, 1]) as [number, number, number];
  return [
    (1 - 2 * (y * y + z * z)) * sx, 2 * (x * y + z * w) * sx, 2 * (x * z - y * w) * sx, 0,
    2 * (x * y - z * w) * sy, (1 - 2 * (x * x + z * z)) * sy, 2 * (y * z + x * w) * sy, 0,
    2 * (x * z + y * w) * sz, 2 * (y * z - x * w) * sz, (1 - 2 * (x * x + y * y)) * sz, 0,
    tx, ty, tz, 1,
  ];
}
